import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  Directive,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpErrorResponse } from '@angular/common/http';
import { EMPTY, catchError, from, map, mergeMap, switchMap } from 'rxjs';
import { DatePipe, NgTemplateOutlet } from '@angular/common';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { ProjectNavComponent } from '../../projects/project-nav/project-nav.component';
import { AvatarComponent } from '../../components/avatar/avatar.component';
import { CodeIdChipComponent } from '../../components/code-id-chip/code-id-chip.component';
import { DefectErfassenDialogComponent } from '../../components/defect-erfassen-dialog/defect-erfassen-dialog.component';
import { canCaptureDefect, withDefect } from '../../components/defect-erfassen-dialog/defect-capture';
import { Defect } from '../../models/defect.model';
import { TestRunService } from '../../services/test-run.service';
import { ProjectIntegrationService } from '../../services/project-integration.service';
import { ResultStatus, TestCaseResult, TestRun, TestRunSource, TestStepResult } from '../../models/test-run.model';
import { ProjectIntegration, ProviderType } from '../../models/project-integration.model';
import { sourceToSystemKey, statusKey, systemMeta } from '../test-results/test-results.types';
import { issueBrowseUrl } from '../../shared/issue-links';
import { mapBugCreateError, TRACKER_ERROR_KEYS } from '../../shared/tracker-error';
import { deriveRunOutcome } from '../../shared/test-run-outcome';
import { formatDurationSeconds } from '../../shared/duration.util';
import { findRouteParam } from '../../shared/route-params';
import { fillStepResult, resolveIterationValues } from '../testlauf-runner/iteration-values';

/** Sources whose runs are produced by a tool rather than a person. */
const AUTOMATED_SOURCES = new Set(['ROBOT', 'JUNIT', 'PIPELINE', 'TCDL', 'IMPORT']);

/** Accepts a Jira key (PROJ-123) or a numeric Redmine id — provider-neutral (NEX-781). */
const ISSUE_KEY_PATTERN = /^([A-Z][A-Z0-9]+-\d+|\d+)$/;

/** Material icon for the run outcome badge. */
const OUTCOME_ICON: Record<ResultStatus, string> = {
  PASSED: 'check',
  FAILED: 'close',
  BLOCKED: 'shield',
  RUNNING: 'sync',
  SKIPPED: 'remove',
  NOT_RUN: 'remove',
};

interface EvidenceItem {
  id: string;
  url: string;
  filename: string;
}

/** One rendered step row: a leaf result plus its display metadata. */
interface DisplayStep {
  result: TestStepResult;
  number: string;
  group: string | null;
  /** Kennung des Modulknotens dieser Gruppe — siehe {@link Leaf}. */
  groupId: string | null;
  /** Status des Modulknotens dieser Gruppe — siehe {@link Leaf}. */
  groupStatus: ResultStatus | null;
  /** Durchgang des Modulknotens dieser Gruppe — siehe {@link Leaf}. */
  groupIteration: number | null;
  indented: boolean;
  /** True on the first row of a new group so the header is drawn once. */
  showGroupHeader: boolean;
}

/** Schritte eines Testfalls unter seinem Kopf, sobald ein Lauf mehrere Testfaelle hat (NEX-1498). */
interface CaseGroup {
  id: string;
  key: string;
  title: string;
  status: ResultStatus;
  steps: DisplayStep[];
}

/** Typisiert den Kontext von `#stepRows`, damit `step` im Template nicht `any` ist. */
@Directive({ selector: 'ng-template[appStepRows]' })
export class StepRowsTemplateDirective {
  static ngTemplateContextGuard(
    _directive: StepRowsTemplateDirective,
    context: unknown,
  ): context is { $implicit: DisplayStep[] } {
    return true;
  }
}

@Component({
  selector: 'app-test-result-detail',
  imports: [
    TranslatePipe,
    DatePipe,
    NgTemplateOutlet,
    StepRowsTemplateDirective,
    RouterLink,
    MatIcon,
    MatProgressSpinner,
    ProjectNavComponent,
    AvatarComponent,
    CodeIdChipComponent,
    DefectErfassenDialogComponent,
  ],
  templateUrl: './test-result-detail.component.html',
  styleUrl: './test-result-detail.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TestResultDetailComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly testRunService = inject(TestRunService);
  private readonly integrationService = inject(ProjectIntegrationService);
  private readonly destroyRef = inject(DestroyRef);

  /** Project's active issue-tracker integration; drives provider-neutral bug links + creation (NEX-781). */
  private readonly activeIntegration = signal<ProjectIntegration | null>(null);
  private readonly boardUrl = computed(() => this.activeIntegration()?.boardUrl ?? null);
  private readonly provider = computed<ProviderType>(() => this.activeIntegration()?.providerType ?? 'JIRA');

  /** Step result whose bug is currently being created; null when idle. */
  protected readonly creatingBugStepId = signal<string | null>(null);
  /** Last bug-create error (step result id + message); null when none. */
  protected readonly bugError = signal<({ stepId: string } & BugError) | null>(null);
  protected readonly toAccountKey = TRACKER_ERROR_KEYS.toAccount;
  /** Server-built deep links of bugs created this session, by step result id. */
  private readonly createdBugUrl = signal<Record<string, string>>({});

  protected readonly statusKey = statusKey;

  protected readonly projectId: string;
  protected readonly runId: string;

  protected readonly loading = signal(true);
  protected readonly loadError = signal(false);
  private readonly run = signal<TestRun | null>(null);

  private readonly evidenceByStepResultId = signal<Record<string, EvidenceItem[]>>({});
  /** URL currently shown in the fullscreen lightbox; null = closed. */
  protected readonly lightboxUrl = signal<string | null>(null);

  protected readonly overviewLink: string[];
  /** Zurueck in die Testfall-Tabelle, aus der Historie und Detail geoeffnet werden. */
  protected readonly overviewQueryParams = { sicht: 'testfaelle' };

  protected readonly runKey = computed(() => this.run()?.key ?? '');
  private readonly source = computed<TestRunSource>(() => this.run()?.source ?? 'MANUAL');
  protected readonly systemKey = computed(() => sourceToSystemKey(this.source()));
  protected readonly system = computed(() => systemMeta(this.systemKey()));
  protected readonly automated = computed(() => AUTOMATED_SOURCES.has(this.source()));
  protected readonly environment = computed(() => this.run()?.environment?.trim() || '—');
  protected readonly executedByName = computed(() => this.run()?.executedByName?.trim() || '');
  protected readonly completedAt = computed(() => this.run()?.completedAt ?? this.run()?.startedAt ?? null);

  /** Primary test case of the run — its title/key head the page. */
  private readonly primaryCase = computed(() => this.run()?.caseResults?.[0] ?? null);
  protected readonly caseTitle = computed(() => this.primaryCase()?.caseTitleSnapshot ?? '');
  protected readonly caseKey = computed(() => this.primaryCase()?.caseKeySnapshot ?? '');

  /** Leaf steps per test case in render order (modules become group headers, not rows). */
  private readonly caseLeaves = computed(() => flattenCaseLeaves(this.run()));
  private readonly leaves = computed(() => this.caseLeaves().flatMap((entry) => entry.leaves));

  protected readonly steps = computed<DisplayStep[]>(() => withGroupHeaders(this.leaves()));

  protected readonly caseGroups = computed<CaseGroup[]>(() =>
    this.caseLeaves().map(({ caseResult, leaves }) => ({
      id: caseResult.id,
      key: caseResult.caseKeySnapshot?.trim() ?? '',
      title: caseResult.caseTitleSnapshot ?? '',
      status: caseResult.status,
      steps: withGroupHeaders(leaves),
    })),
  );
  /** Mehr als ein Testfall: Kopf nennt den Lauf, Schritte stehen je Testfall (NEX-1498). */
  protected readonly multiCase = computed(() => this.caseGroups().length > 1);
  protected readonly caseCount = computed(() => this.caseGroups().length);
  protected readonly runName = computed(() => this.run()?.name?.trim() ?? '');

  protected readonly total = computed(() => this.leaves().length);
  protected readonly passed = computed(() => this.countStatus('PASSED'));
  protected readonly failed = computed(() => this.countStatus('FAILED'));
  protected readonly blocked = computed(() => this.countStatus('BLOCKED'));
  protected readonly skipped = computed(() => this.countStatus('SKIPPED'));

  /** Frozen run outcome; derived from the steps when the backend left it undecided. */
  protected readonly outcome = computed<ResultStatus>(() => this.run()?.outcome ?? this.deriveOutcome());
  protected readonly outcomeIcon = computed(() => OUTCOME_ICON[this.outcome()]);

  protected readonly runDuration = computed(() => formatDurationSeconds(this.run()?.durationSeconds ?? null));

  protected readonly unmatchedTests = computed(() => (this.automated() ? (this.run()?.unmatchedTests ?? 0) : 0));
  protected readonly unmatchedTestsKey = computed(() =>
    this.unmatchedTests() === 1 ? 'testResults.detail.unmatchedTestsOne' : 'testResults.detail.unmatchedTestsOther',
  );
  /**
   * Ohne executionId oeffnet die CI-Zuordnung den neuesten Durchlauf — und genau den
   * zeigt diese Seite.
   */
  protected readonly ciMappingLink: string[];

  constructor() {
    this.projectId = this.findParam('projectId') ?? '';
    this.runId = this.route.snapshot.paramMap.get('runId') ?? '';
    this.overviewLink = ['/projects', this.projectId, 'test-results'];
    this.ciMappingLink = ['/projects', this.projectId, 'test-runs', this.runId, 'ci-mapping'];
    this.destroyRef.onDestroy(() => {
      for (const items of Object.values(this.evidenceByStepResultId())) {
        for (const item of items) URL.revokeObjectURL(item.url);
      }
    });
  }

  ngOnInit(): void {
    this.testRunService
      .get(this.projectId, this.runId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (run) => {
          this.run.set(run);
          this.loading.set(false);
          this.loadEvidence();
        },
        error: () => {
          this.loadError.set(true);
          this.loading.set(false);
        },
      });
    this.loadIntegration();
  }

  /** Resolves the project's active issue integration so bug links/creation target the right provider (NEX-781). */
  private loadIntegration(): void {
    this.integrationService
      .list(this.projectId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (integrations) => {
          const active =
            integrations.find((i) => i.active && (i.providerType === 'JIRA' || i.providerType === 'REDMINE')) ?? null;
          this.activeIntegration.set(active);
        },
        error: () => this.activeIntegration.set(null),
      });
  }

  protected stepDuration(result: TestStepResult): string {
    return formatDurationSeconds(result.durationSeconds);
  }

  protected evidenceFor(stepResultId: string): EvidenceItem[] {
    return this.evidenceByStepResultId()[stepResultId] ?? [];
  }

  protected openLightbox(url: string): void {
    this.lightboxUrl.set(url);
  }

  protected closeLightbox(): void {
    this.lightboxUrl.set(null);
  }

  /** Linked bug key of a step, but only when it is well-formed — otherwise no jump target. */
  protected linkedBugKey(result: TestStepResult): string | null {
    const key = result.linkedBugJiraKey?.trim();
    return key && ISSUE_KEY_PATTERN.test(key) ? key : null;
  }

  /** Whether a bug is currently being created for this step. */
  protected isCreatingBug(result: TestStepResult): boolean {
    return this.creatingBugStepId() === result.id;
  }

  /** The bug-create error for this step, or null. */
  protected bugErrorFor(result: TestStepResult): BugError | null {
    const error = this.bugError();
    return error?.stepId === result.id ? error : null;
  }

  /** Jumps to the linked bug at the correct provider, in a new tab (provider-neutral deep link). */
  protected openLinkedBug(result: TestStepResult): void {
    const key = this.linkedBugKey(result);
    if (!key) return;
    const url = this.createdBugUrl()[result.id] ?? issueBrowseUrl(this.provider(), key, this.boardUrl());
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  /**
   * Creates a bug from a failed step result in the active provider (NEX-776/781) and
   * shows the resulting link. Redmine is writable; a Jira-only project answers 501.
   */
  protected createBug(result: TestStepResult): void {
    if (this.creatingBugStepId()) return;
    this.creatingBugStepId.set(result.id);
    this.bugError.set(null);
    this.testRunService
      .createBugFromStepResult(this.projectId, result.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (bug) => {
          this.createdBugUrl.update((map) => ({ ...map, [result.id]: bug.browseUrl }));
          this.applyLinkedBugKey(result.id, bug.key);
          this.creatingBugStepId.set(null);
        },
        error: (err: HttpErrorResponse) => {
          this.bugError.set({ stepId: result.id, ...mapBugError(err, this.boardUrl()) });
          this.creatingBugStepId.set(null);
        },
      });
  }

  protected canCaptureDefect(result: TestStepResult): boolean {
    return canCaptureDefect(result.kind, result.status);
  }

  /** Der Schritt, fuer den der Erfassen-Dialog offen ist. */
  protected readonly defectStepId = signal<string | null>(null);

  protected captureDefect(result: TestStepResult): void {
    this.defectStepId.set(result.id);
  }

  protected onDefectCreated(defect: Defect): void {
    const stepResultId = this.defectStepId();
    this.defectStepId.set(null);
    if (stepResultId) {
      this.updateStepResult(stepResultId, (step) => ({ ...step, defects: withDefect(step.defects, defect) }));
    }
  }

  private applyLinkedBugKey(stepResultId: string, key: string): void {
    this.updateStepResult(stepResultId, (step) => ({ ...step, linkedBugJiraKey: key }));
  }

  /** Immutably replaces the matching step result of the loaded run. */
  private updateStepResult(stepResultId: string, change: (step: TestStepResult) => TestStepResult): void {
    const current = this.run();
    if (!current?.caseResults) return;
    this.run.set({
      ...current,
      caseResults: current.caseResults.map((caseResult) => ({
        ...caseResult,
        stepResults: caseResult.stepResults?.map((step) => (step.id === stepResultId ? change(step) : step)) ?? null,
      })),
    });
  }

  private countStatus(status: ResultStatus): number {
    return this.leaves().filter((leaf) => leaf.result.status === status).length;
  }

  private deriveOutcome(): ResultStatus {
    return deriveRunOutcome(this.leaves().map((leaf) => leaf.result.status));
  }

  // Nachweise read-only laden: fehlende Anhänge oder fehlgeschlagene Requests werden
  // still übersprungen (kein Bild, kein Fehler-Banner) — die Seite bleibt lesbar.
  private loadEvidence(): void {
    for (const leaf of this.leaves()) {
      const stepResultId = leaf.result.id;
      this.testRunService
        .listStepResultAttachments(this.projectId, stepResultId)
        .pipe(
          switchMap((attachments) => {
            if (attachments.length === 0) return EMPTY;
            return from(attachments).pipe(
              mergeMap((attachment) =>
                this.testRunService.downloadStepResultAttachment(this.projectId, stepResultId, attachment.id).pipe(
                  map((blob) => ({ id: attachment.id, filename: attachment.originalFilename, blob })),
                  catchError(() => EMPTY),
                ),
              ),
            );
          }),
          catchError(() => EMPTY),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe(({ id, filename, blob }) => {
          const url = URL.createObjectURL(blob);
          this.evidenceByStepResultId.update((map) => ({
            ...map,
            [stepResultId]: [...(map[stepResultId] ?? []), { id, url, filename }],
          }));
        });
    }
  }

  private findParam(name: string): string | null {
    return findRouteParam(this.route, name);
  }
}

interface BugError {
  key: string;
  params: Record<string, string>;
  accountLink: boolean;
}

/**
 * Maps a bug-create HTTP failure to a user-facing message (NEX-781). Tracker rejections with
 * their own code come first (NEX-1354), so only a 409 without code means "already linked". A 412
 * has two causes: the project lacks an active tracker integration (`NO_ACTIVE_INTEGRATION`,
 * NEX-1117) or the user lacks a personal token.
 */
function mapBugError(err: HttpErrorResponse, boardUrl: string | null): BugError {
  return mapBugCreateError(err, boardUrl) ?? { key: bugErrorKey(err), params: {}, accountLink: false };
}

function bugErrorKey(err: HttpErrorResponse): string {
  switch (err.status) {
    case 501:
      return 'testResults.detail.bug.notSupported';
    case 409:
      return 'testResults.detail.bug.alreadyLinked';
    case 412:
      return (err.error as { code?: string } | null)?.code === 'NO_ACTIVE_INTEGRATION'
        ? 'testResults.detail.bug.noIntegration'
        : 'testResults.detail.bug.tokenMissing';
    default:
      return 'testResults.detail.bug.createError';
  }
}

interface Leaf {
  result: TestStepResult;
  number: string;
  group: string | null;
  /**
   * Kennung des Modulknotens, nicht sein Name: Ein Modul mit Datenblatt laeuft je Zeile
   * einmal, und alle Durchlaeufe heissen gleich. Nach dem Namen gebrochen stuenden sie
   * unter einer Ueberschrift — mit dem Status des ersten Durchlaufs ueber den Zeilen des
   * zweiten (NEX-1323).
   */
  groupId: string | null;
  /**
   * Status des Modulknotens, zu dem diese Zeile gehoert. Er wird aus den Schritten
   * darunter abgeleitet (NEX-1323) — ohne ihn stuende ueber einer Gruppe bestandener
   * Schritte dauerhaft "nicht ausgefuehrt".
   */
  groupStatus: ResultStatus | null;
  /**
   * Durchgang des Modulknotens ({@code iterationIndex}, 1-basiert wie die Zeile im
   * Datenblatt); {@code null} ohne Datenblatt. Unterscheidet die gleich benannten Koepfe
   * der Durchlaeufe (NEX-1677).
   */
  groupIteration: number | null;
  indented: boolean;
}

/**
 * Flattens the nested run into leaf step rows per test case, both in render order.
 * Step results arrive flat in {@code orderIndex} order with MODULE nodes preceding
 * their children; MODULE titles become the group label of their descendants and
 * are not themselves rendered as rows. Numbering restarts per test case.
 */
function flattenCaseLeaves(run: TestRun | null): { caseResult: TestCaseResult; leaves: Leaf[] }[] {
  if (!run?.caseResults) return [];
  return [...run.caseResults]
    .sort((a, b) => a.orderIndex - b.orderIndex)
    .map((caseResult) => {
      const sorted = [...(caseResult.stepResults ?? [])].sort((a, b) => a.orderIndex - b.orderIndex);
      const steps = resolveIterationValues(sorted, fillStepResult);
      const byParent = new Map<string | null, TestStepResult[]>();
      for (const step of steps) {
        const key = step.parentStepResultId ?? null;
        (byParent.get(key) ?? byParent.set(key, []).get(key)!).push(step);
      }
      const leaves: Leaf[] = [];
      collectLeaves(
        byParent,
        null,
        '',
        { group: null, groupId: null, groupStatus: null, groupIteration: null },
        leaves,
      );
      return { caseResult, leaves };
    });
}

/** Marks the first row of each module group so its header is drawn once. */
function withGroupHeaders(leaves: Leaf[]): DisplayStep[] {
  let previousGroupId: string | null = null;
  return leaves.map((leaf) => {
    const showGroupHeader = !!leaf.group && leaf.groupId !== previousGroupId;
    previousGroupId = leaf.groupId;
    return { ...leaf, showGroupHeader };
  });
}

/** Die Gruppe, unter der eine Zeile steht: der naechste Modulknoten oberhalb. */
type GroupInfo = Pick<Leaf, 'group' | 'groupId' | 'groupStatus' | 'groupIteration'>;

function collectLeaves(
  byParent: Map<string | null, TestStepResult[]>,
  parentId: string | null,
  prefix: string,
  groupInfo: GroupInfo,
  leaves: Leaf[],
): void {
  const children = byParent.get(parentId) ?? [];
  children.forEach((result, index) => {
    const number = prefix ? `${prefix}.${index + 1}` : `${index + 1}`;
    if (result.kind === 'MODULE') {
      collectLeaves(
        byParent,
        result.id,
        number,
        {
          group: result.descriptionSnapshot ?? groupInfo.group,
          groupId: result.id,
          groupStatus: result.status,
          groupIteration: result.iterationIndex,
        },
        leaves,
      );
    } else {
      leaves.push({ result, number, ...groupInfo, indented: number.includes('.') });
      collectLeaves(byParent, result.id, number, groupInfo, leaves);
    }
  });
}
